import get from 'lodash.get';
import {DEFAULT_DELIVERY_RADIUS} from '../config/global';
import {toBool} from '../reducers/configReducer';
import {getKioskMode,
  isKioskMode as isKioskSelector,
} from '../selectors/viewSelectors';
import {kioskModeType, CUSTOMER_TYPE_ALLOW} from '../config/const';
import {TICKET_TYPE} from '../const/ticket_type';
import { isDelivery as isDeliverySelector } from '../reducers/addressReducer';
import { getCurrentCustomerType } from './customer_type_selector';
import CustomerType from '../const/customer_type';

export const isPhoneSelector = state => state.config.isPhone;

export const isApplePhoneSelector = state => state.config.is_iPhone;

export const deliveryRadiusSelector = (state) => get(state, 'config.ECOMMERCE_ADDRESS_RADIUS', DEFAULT_DELIVERY_RADIUS);

export const kioskNotesSelector = state => [];

// pull configs per onlinemenu feature
export const dposSelector = state => {};

// readonly menu, autoscroll
export const mainBoardSelector = (state) => ({
  ageGate: "false",
  cart: "false",
  orderType: [],
  zodokaPayment: "false",
  paidOrder: "false",
  slowScrolling: "true",
  startShoppingPopUp: "false",
  membershipAgreement: "false",
  unIdentifiedCustomer: "false",
  search: "true",
  switchToTevanoPay: "false",
  logoAndItemTypeBar: "scroll_off",
  homeIconCustomLink: "",
});

export const onlinemenuSelector = state => JSON.parse(state.config.ONLINEMENU);
export const paymentKioskSelector = state => {};
export const selfServiceKioskSelector = state => {};

export const isDposMode = (state) => {
  const mode = getKioskMode(state);
  return mode === kioskModeType.DPOS;
};

export const isPaymentKiosk = (state) => {
  const mode = getKioskMode(state);
  return mode === kioskModeType.PAYMENT_KIOSK;
};

export const isMainBoard = (state) => {
  const mode = getKioskMode(state);
  return mode === kioskModeType.MAIN_BOARD;
};

export const isSelfServiceMode = state => {
  const mode = getKioskMode(state);
  return mode === kioskModeType.SELF_SERVICE_KIOSK;
};

export const isKioskWithPurchasing = state => {
  return isDposMode(state) || isPaymentKiosk(state) || isSelfServiceMode(state);
}

export const allowPhoneNumberConfigSelector = (state) => get(state, 'config.ALLOW_PHONE_NUMBER_LOGIN') === '1';
export const isOnlineMenu = (state) => !isDposMode(state)
                                        && !isPaymentKiosk(state)
                                        && !isSelfServiceMode(state)
                                        && !isMainBoard(state);
export const ageGateNumValidSelector = state => !isNaN(state.config.AGE_GATE);

export const getConfigPerMode = (state) => {
  if (!state.config.configFetched) {
    return null;
  }
  if (isOnlineMenu(state)) {
    return onlinemenuSelector(state);
  }
  if (isPaymentKiosk(state)) {
    return paymentKioskSelector(state);
  }
  if (isDposMode(state)) {
    return dposSelector(state);
  }
  if (isSelfServiceMode(state)) {
    return selfServiceKioskSelector(state);
  }
  if (isMainBoard(state)) {
    return mainBoardSelector(state);
  }
};

export const isAgeGateSelector = state => {
  if (!state.config.configFetched) {
    return false;
  }
  if (isOnlineMenu(state) && state.user.tokens.token) {
    return false;
  }
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }

  return toBool(config.ageGate) && ageGateNumValidSelector(state);
};

export const isCartSelector = state => {
  if (!state.config.configFetched) {
    return false;
  }
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }

  return toBool(config.cart);
};

const isOnlinemenuOrderType = (type, state) => {
  if (type === 'pickup') {
    return get(state, 'config.ECOMMERCE_TYPE_INSTORE', 0)
  }
  if (type === 'delivery') {
    return get(state, 'config.ECOMMERCE_TYPE_DELIVERY', 0)
  }
  if (type === 'express') {
    return get(state, 'ECOMMERCE_TYPE_EXPRESS', 0)
  }
}

export const isOrderType = (type, state) => {
  if (!state.config.configFetched) {
    return false;
  }
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }

  // gotcha due to config conflicts for onlinemenu
  // ECOMMERCE_TYPE_DELIVERY
  // ECOMMERCE_TYPE_INSTORE
  // ECOMMERCE_TYPE_EXPRESS
  // and new code
  // 'ONLINEMENU', '{"ageGate":"true","cart":"true","orderType":["express"] ... }
  
  if (isOnlineMenu(state)) {
    return isOnlinemenuOrderType(type, state);
  }

  return config.orderType[type];
};

const getOnlinemenuOrderTypes = (state) => {
  let types = [];
  if (toBool(state.config.ECOMMERCE_TYPE_INSTORE)) {
    types.push('pickup');
  }
  if (toBool(state.config.ECOMMERCE_TYPE_DELIVERY)) {
    types.push('delivery');
  }
  if (toBool(state.config.ECOMMERCE_TYPE_EXPRESS)) {
    types.push('express');
  }
}

export const getOrderTypes = (state) => {
  if (!state.config.configFetched) {
    return false;
  }
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }

  if (isOnlineMenu(state)) {
    return getOnlinemenuOrderTypes(state);
  }

  return config.orderType;
};

export const getPrimaryOrderType = (state) => {
  if (!state.config.configFetched) {
    return false;
  }
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  if (isOnlineMenu(state)) {
    return getOnlinemenuOrderTypes(state).length ? getOnlinemenuOrderTypes(state)[0].toUpperCase() : '';
  }

  return config.orderType.length ? config.orderType[0].toUpperCase() : '';
};

export const isPickup = (state) => toBool(isOrderType('pickup', state));
export const isDelivery = (state) => toBool(isOrderType('delivery', state));
export const isExpress = (state) => isOrderType('express', state);
export const isZodokaPayment = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }

  return config.zodokaPayment;
};

export const isPaidOrder = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }

  return toBool(config.paidOrder);
};

export const isSlowScrolling = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return toBool(config.slowScrolling);
};

export const isStartShoppingPopUp = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return toBool(config.startShoppingPopUp);
};

export const isMembershipAgreement = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return toBool(config.membershipAgreement);
};

export const isUnidentifiedCustomer = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return toBool(config.unIdentifiedCustomer);
};

export const isSearch = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return toBool(config.search);
};

export const isSwitchToTevanoPay = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return toBool(config.switchToTevanoPay);
};

export const isLogoAndItemTypeBarFixed = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return config.logoAndItemTypeBar === 'fixed';
};

export const isAutoScrollEnabledByDefault = state => {
  return isMainBoard(state)
}

export const homeIconCustomLink = state => {
  const config = getConfigPerMode(state);
  if (!config) {
    return false;
  }
  return config.homeIconCustomLink;
};


export const rewardsEnabledConfigSelector = (state) => {
  const value = get(state, 'config.REWARD_MODE');
  return Boolean(value === '1' || value === '2');
};

export const isFacebookPixel = state => {
  const value = get(state, 'config.FACEBOOK_PIXEL');
  return Boolean(value === '1');
};

export const isShippingForm = state => {
  return !isPaymentKiosk(state)
    && !isSelfServiceMode(state)
    && !isDposMode(state)
    && !isMainBoard(state);
};

export const isPaymentKioskEnabled = state => {
  return false;
};

export const warningSelector = state => {
  const value = get(state, 'config.P65WARNINGS');
  return value;
};

export const getPotsApiConfig = state => {
  if (isPaymentKioskEnabled(state)) {
    return {};
  }
  return null;
};

export const getPaymentKioskConfiguration = state => {
  return {};
};

export const getPaymentKioskThankYouMessageTimeout = state => {};

export const getGoogleMapsApiKey = state => get(state, 'config.googleMapsApiKey');
export const getGoogleAnalyticsTrackingId = state => get(state, 'config.GOOGLE_ANALYTICS_TRACKING_ID', null);

export const isPostTaxEnabled = state => toBool(state.config.POST_TAX_ENABLED);

export const getReasonsSelector = state => get(state, 'config.REASON_LIST');

export const getLogoUrlLink = state => get(state, 'config.LOGO_URL');
export const getDispensaryName = state => get(state, 'config.DISPENSARY_FULL_NAME');
export const getSearchIconColor = state => get(state, 'config.MENU_SEARCH_ICON_COLOR');
export const getPurchaseLimitsIconColor = state => get(state, 'config.MENU_PURCHASE_LIMITS_ICON_COLOR');
export const getCartIconColor = state => get(state, 'config.MENU_CART_ICON_COLOR');
export const getHamburgerIconColor = state => get(state, 'config.MENU_HAMBURGER_ICON_COLOR');
export const getCategoryFilterColor = state => get(state, 'config.CATEGORY_FILTER_COLOR');
export const getCategoryFilterTextColor = state => get(state, 'config.CATEGORY_FILTER_TEXT_COLOR');

export const isOrderingHours = config => get(config, 'IS_ORDERING_HOURS');

export const isRootSalesChannelEnabled = (state) => {
  return toBool(get(state, 'salesChannelMetadata.isRoot', false));
};

export const isSalesChannelEnabled = (state) => {
  return toBool(get(state, 'config.SBL_ENABLED', 0));
};

export const isSalesChannelNameProvided = (state) => {
  return !!get(state, 'salesChannel.name', undefined);
};

export const isSalesChannelFulfillmentMethodOn = (state) => {
  const salesChannelMeta = get(state.config, 'salesChannelMetadata');

  return salesChannelMeta && (salesChannelMeta.isPickup || salesChannelMeta.isDelivery);
};

export const isSalesChannelActive = (state) => {
  return get(state.config, 'salesChannelMetadata.isActive', false);
};

export const getColors = state => state.config.colors;

export const getCurrentTicketType = (state) => {
  if (isKioskSelector(state) || isDposMode(state) || isSelfServiceMode(state)) {
    return getPrimaryOrderType(state);
  }
  return isDeliverySelector(state) ? TICKET_TYPE.DELIVERY : TICKET_TYPE.PICKUP;
};

export const isProductsByCustomerTypeEnabled = state => get(state, 'config.PRODUCTS_BY_CUSTOMER_TYPE', '0') === '1';

export const isCaptchaValidationEnabled = state => get(state, 'config.CAPTCHA_VALIDATION_ENABLED', '0') === '1';

export const isForgotPasswordByEmailEnabled = state => get(state, 'config.ECOMMERCE_FORGOT_PASSWORD_BY_EMAIL_ENABLED', '0') === '1';

export const isMedCartRecItemsAllowed = state => get(state, 'config.ECOM_MED_CART_REC_ITEMS', '0') === '1';

export const getCategories = (state) => {
  return get(state, 'config.categories', []);
};

export const getCollections = (state) => {
  const collections = get(state, 'config.collections', []);
  collections.sort((a, b) => a.order - b.order);

  return collections;
};

export const getSubCategories = (state) => {
  return get(state, 'config.subCat', []);
};

export const getBrands = (state) => {
  return get(state, 'config.brands', []);
};

export const getSubtypes = (state) => {
  return get(state, 'config.subTypes', []);
};

export const getClassifications = (state) => {
  return get(state, 'config.classifications', []);
};

export const getFlavors = (state) => {
  return get(state, 'config.flavors', []);
};

export const getEffects = (state) => {
  return get(state, 'config.effects', []);
};

export const getGeneralAttributes = (state) => {
  return get(state, 'config.general', []);
};

export const getAchProviderPublishableKey = (state) => {
  return get(state, 'config.ACH_PROVIDER_PUBLISHABLE_KEY', '');
};

export const getAchProviderEnvironment = (state) => {
  return get(state, 'config.ACH_PROVIDER_ENVIRONMENT', '');
};

export const getConfigValue = (state, config) => {
  return get(state, `config[${config}]`)
};

export const isTaxByPackageCustomerType = state => toBool(get(state, 'config.TAX_BY_PACKAGE_CUSTOMER_TYPE'), '0');

export const isEcommerceSearchBarEnabled = state => toBool(get(state, 'config.ECOMMERCE_SEARCH_SUGGESTIONS_BAR_ENABLED'), '0');

export const isEcommerceCollectionBarEnabled = state => toBool(get(state, 'config.ECOMMERCE_COLLECTION_BAR_ENABLED'), '0');

export const isEcomAchPaymentEnabled = state => toBool(get(state, 'config.STRONGHOLD_ECOMMERCE_ACH_PAYMENT_ENABLED'), '0');

export const isEcommerceCustomerTypeOptionEnabled = state => toBool(get(state, 'config.ECOMMERCE_CUSTOMER_TYPE_OPTION_ENABLED'), '0');

export const isCustomerTypeOptionEnabled = state => {
  return getConfigValue(state, CUSTOMER_TYPE_ALLOW) === CustomerType.ALL
    && isProductsByCustomerTypeEnabled(state) && isEcommerceCustomerTypeOptionEnabled(state);
}

export const getPrimaryColor = state => get(state, 'config.primaryColor', '000');
export const getSecondaryColor = state => get(state, 'config.secondaryColor', '000');

export const isHowDidYouHearAboutUsEnabled = state => toBool(get(state, 'config.HOW_DID_YOU_HEAR_ABOUT_US_ENABLED'), '0');

export const isStrictMedicalInventoryRestrictionEnabled = state => toBool(get(state, 'config.STRICT_MEDICAL_INVENTORY_RESTRICTION_ENABLED'), '0');

export const isEmailVerificationRequired = state => toBool(get(state, 'config.EMAIL_VERIFICATION_REQUIRED'), '0');

export const isEmailValidationEnabled = state => toBool(get(state, 'config.EMAIL_VALIDATION_ENABLED'), '0');



// WEBPACK FOOTER //
// ./src/selectors/configSelectors.js